// ABOUTME: SPEC-82 §4.8 — the top-left hamburger and its popover (Vesper / Vélos / Privacy). Presentation only: the hook owns
// ABOUTME: entries and state, the Views button owns the switch. Reuses the mobile menu chip + glyph, scaled by --u.

import React from 'react';
import { Watch } from 'lucide-react';
import { MobileChip } from '../../ReelChrome/MobileChrome';
import { GLYPHS, RAIL_LEFT_PCT, RAIL_TOP_PCT, topOffset, u, uMin } from '../../ReelChrome/railGeometry';
import { MENU_CHIP, MENU_GLYPH, WORDMARK as MOBILE_WORDMARK, mu, muMin, safeTop } from '../../ReelChrome/mobileGeometry';
import { ARIA } from '../../../uiExperiment/copy';
import { MENU, MENU_UNIT } from './collectionMenuTokens';
import MenuConsentPanel from './MenuConsentPanel';

const scaleOf = (composition) => (composition === 'desktop' ? u : mu);

// Top-left corner per composition. Desktop: where the rail used to be (the rail moved right, D-16).
// Mobile: the mirror of the chip stack's right inset.
const placement = (composition) => (composition === 'desktop'
  ? { left: `${RAIL_LEFT_PCT}%`, top: topOffset(RAIL_TOP_PCT) }
  : { left: `calc(${MENU_CHIP.rightInset} * var(--u) + var(--reel-safe-l))`, top: safeTop(MENU_CHIP.top) });

// Mobile: the popover must stop short of the centred wordmark, so rows wrap instead of running under it. Half the viewport, less half
// the wordmark, the menu's own left inset and a gap. Desktop has the whole width between the corner and the wordmark.
const popoverMaxWidth = (composition) => (composition === 'desktop'
  ? undefined
  : `calc(50vw - ${(MOBILE_WORDMARK.w / 2 + MENU.wordmarkGap + MENU_CHIP.rightInset)} * var(--u) - var(--reel-safe-l))`);

function RecordingDot() {
  return (
    <span
      data-testid="recording-indicator"
      aria-label="Recording active"
      className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse motion-reduce:animate-none ring-2 ring-black/40"
    />
  );
}

function MenuRow({ entry, composition, onSelect }) {
  const scale = scaleOf(composition);
  const font = composition === 'desktop' ? uMin(MENU.font, MENU.minFontPx) : muMin(MENU.font, MENU.minFontPx);
  const inert = entry.current || entry.disabled;
  return (
    <button
      type="button"
      data-vf={`menu-${entry.id}`}
      aria-current={entry.current ? 'true' : undefined}
      aria-disabled={inert ? 'true' : undefined}
      onClick={() => { if (!inert) { entry.onPress(); onSelect(); } }}
      className={`reel-touch-target relative flex items-center text-left ${composition === 'desktop' ? 'whitespace-nowrap' : ''} text-reel-chrome-textPrimary font-reel-medium`}
      style={{
        minHeight: `max(${MENU.minRowPx}px, ${scale(MENU.rowH)})`, gap: scale(MENU.gap), padding: `0 ${scale(MENU.padX)}`,
        fontSize: font, lineHeight: 1.1, fontWeight: entry.current ? MENU.currentWeight : MENU.otherWeight,
        opacity: entry.disabled && !entry.current ? MENU.disabledOpacity : 1, cursor: inert ? 'default' : 'pointer',
      }}
    >
      {entry.kind === 'privacy'
        ? <img src={GLYPHS.privacy.src} alt="" aria-hidden="true" style={{ width: scale(MENU.iconW), height: 'auto' }} />
        : <Watch aria-hidden="true" style={{ width: scale(MENU.watchIconSize), height: scale(MENU.watchIconSize) }} />}
      <span>{entry.label}</span>
    </button>
  );
}

/**
 * @param {{ composition: 'desktop'|'mobile', menu: object, recording?: boolean, privacy?: object }} props `menu` is
 *   useCollectionMenu()'s return; `privacy` is MenuConsentPanel's (SPEC-82 Rev 3: the consent panel hangs under the hamburger)
 */
export default function CollectionMenu({ composition, menu, recording = false, privacy = null }) {
  if (!menu.visible) return null;
  const scale = scaleOf(composition);
  // SPEC-82 review M2 (layering): the consent panel and the popover open into the same column under the hamburger. On mobile the
  // panel is absolutely placed over it (the popover went UNDER the panel); on desktop the popover pushes the panel down and off the
  // bottom of a laptop viewport (real app, 1366x768: panel bottom 800 > 768). One panel at a time: opening the menu folds it away first.
  const onChipClick = () => {
    if (!menu.open && privacy?.open) privacy.onCollapse();
    menu.toggle();
  };
  return (
    <div
      ref={menu.rootRef}
      data-vf="menu"
      className="absolute flex flex-col items-start pointer-events-auto"
      style={{ pointerEvents: 'auto', '--u': MENU_UNIT[composition], ...placement(composition), gap: `max(${scale(MENU.popoverGap)}, ${MENU.minChipGapPx}px)` }}
    >
      <MobileChip
        glyph={MENU_GLYPH}
        label={menu.open ? ARIA.menuClose : ARIA.menu}
        expanded={menu.open}
        onClick={onChipClick}
        glyphStyle={{
          position: 'absolute', left: mu(MENU_GLYPH.left), top: mu(MENU_GLYPH.top), width: mu(MENU_GLYPH.w), height: mu(MENU_GLYPH.h),
        }}
      >
        {/* VER-40: the recording disclosure rides on whichever control opens privacy — the menu itself while it is shut. */}
        {recording && !menu.open && <RecordingDot />}
      </MobileChip>
      {menu.open && (
        <div
          data-vf="menu-popover"
          role="group"
          aria-label="Menu"
          className="flex flex-col bg-reel-chrome-panel backdrop-blur-reel-chrome"
          // `relative` + z-index: the consent panel is positioned (absolute) and would otherwise paint over this unpositioned popover.
          style={{ position: 'relative', zIndex: 1, borderRadius: scale(MENU.radius), overflow: 'hidden', maxWidth: popoverMaxWidth(composition) }}
        >
          {menu.entries.map((entry) => <MenuRow key={entry.id} entry={entry} composition={composition} onSelect={menu.close} />)}
        </div>
      )}
      <MenuConsentPanel composition={composition} privacy={privacy} />
    </div>
  );
}
